import { Volume2, VolumeX } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";

function FullScreenVideo({
  videoSrc,
  videoPreview,
  mobileVideo,
  mobileVideoPreview,
  autoplay = false,
  className = "",
}: {
  videoSrc: string;
  videoPreview: string;
  mobileVideo?: string;
  mobileVideoPreview?: string;
  autoplay?: boolean;
  className?: string;
}) {
  const videoRef = useRef<any>(null);
  const { t } = useTranslation();
  const [activateControls, setActivateControls] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(autoplay);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const currentVideoSrc = useMemo(
    () => (isMobile && mobileVideo ? mobileVideo : videoSrc),
    [isMobile, mobileVideo, videoSrc]
  );
  const currentVideoPreview = useMemo(
    () => (isMobile && mobileVideoPreview ? mobileVideoPreview : videoPreview),
    [isMobile, mobileVideoPreview, videoPreview]
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleError = () => {
      setIsLoading(false);
    };

    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("error", handleError);

    return () => {
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("error", handleError);
    };
  }, [currentVideoSrc]);

  const handlePlay = async () => {
    if (videoRef.current) {
      setActivateControls(true);
      try {
        await videoRef.current.load();
        await videoRef.current.play();
      } catch (error) {
        console.error("Errore durante la riproduzione del video:", error);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  return (
    <div className="relative">
      <video
        ref={videoRef}
        autoPlay={autoplay}
        muted={isMuted}
        loop={autoplay}
        playsInline
        src={currentVideoSrc}
        controls={activateControls}
        preload="none"
        className={className}
      >
        Il tuo browser non supporta il tag video.
      </video>
      {autoplay && (
        <button
          onClick={toggleMute}
          className="absolute bottom-4 right-4 z-10 p-3 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full text-white transition-all duration-200 flex items-center justify-center"
          aria-label={isMuted ? t("Attiva audio") : t("Disattiva audio")}
        >
          {isMuted ? (
            <VolumeX size={24} />
          ) : (
            <Volume2 size={24} />
          )}
        </button>
      )}
      {!activateControls && !autoplay && (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              className="absolute w-full h-full object-cover object-center"
              src={currentVideoPreview}
              alt=""
            />
            <Button
              variant="muted"
              onClick={handlePlay}
              className="relative z-10"
            >
              <span>{t("Guarda il video")}</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FullScreenVideo;
